import { useState, useEffect, useCallback, useRef } from "react";
import type {
  CollectionResponse,
  HustleState,
  PackCard,
  PackDefinition,
  PackId,
  PackResult,
  PackSummary,
  UserData,
} from "./types";
import { DEFAULT_PACK_ID, PACK_DEFINITIONS, TICK_INTERVAL_MS } from "./types";
import { NavBar } from "./components/NavBar";
import { PackShop } from "./components/PackShop";
import { PackOpening } from "./components/PackOpening";
import { Collection } from "./components/Collection";
import { HustleView } from "./components/HustleView";
import { BattleView } from "./components/BattleView";
import { Button } from "./components/ui/button";
import "./index.css";

type View = "shop" | "opening" | "collection" | "hustle" | "battle";

export function App() {
  const [view, setView] = useState<View>("shop");
  const [user, setUser] = useState<UserData | null>(null);
  const [packs, setPacks] = useState<PackDefinition[]>(PACK_DEFINITIONS);
  const [packCards, setPackCards] = useState<PackCard[] | null>(null);
  const [lastOpenedPack, setLastOpenedPack] = useState<PackSummary | null>(null);
  const [collection, setCollection] = useState<CollectionResponse | null>(null);
  const [hustleData, setHustleData] = useState<HustleState | null>(null);
  const [loading, setLoading] = useState(false);
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [hustleLoading, setHustleLoading] = useState(false);
  const [appError, setAppError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollInFlightRef = useRef(false);

  const dismissError = useCallback(() => {
    setAppError(null);
  }, []);

  const showError = useCallback((message: string) => {
    setAppError(message);
  }, []);

  const handleNavigate = useCallback((nextView: View) => {
    setView(nextView);
    dismissError();
  }, [dismissError]);

  const fetchUser = useCallback(async () => {
    try {
      const res = await fetch("/api/user");
      if (!res.ok) throw new Error("Failed to fetch user");
      const data = (await res.json()) as UserData;
      setUser(data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchCollection = useCallback(async () => {
    setCollectionLoading(true);
    try {
      const res = await fetch("/api/collection");
      if (!res.ok) throw new Error("Failed to fetch collection");
      const data = (await res.json()) as CollectionResponse;
      setCollection(data);
    } catch (error) {
      console.error(error);
    } finally {
      setCollectionLoading(false);
    }
  }, []);

  const fetchHustle = useCallback(async () => {
    setHustleLoading(true);
    try {
      const res = await fetch("/api/hustle");
      if (!res.ok) throw new Error("Failed to fetch hustle state");
      const data = (await res.json()) as HustleState;
      setHustleData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setHustleLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch("/api/packs");
        if (!res.ok) throw new Error("Failed to fetch packs");
        const data = (await res.json()) as { packs?: PackDefinition[] };
        if (Array.isArray(data.packs) && data.packs.length > 0) {
          setPacks(data.packs);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  useEffect(() => {
    if (view === "collection") {
      fetchCollection();
    }
    if (view === "hustle" || view === "battle") {
      fetchHustle();
    }
  }, [view, fetchCollection, fetchHustle]);

  // Poll coins on hustle tab
  useEffect(() => {
    if (view === "hustle") {
      pollRef.current = setInterval(() => {
        if (pollInFlightRef.current) return;
        pollInFlightRef.current = true;
        void fetchUser().finally(() => {
          pollInFlightRef.current = false;
        });
      }, TICK_INTERVAL_MS);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        pollInFlightRef.current = false;
      };
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      pollInFlightRef.current = false;
    }
  }, [view, fetchUser]);

  const buyPack = useCallback(async (packId: PackId) => {
    setLoading(true);
    dismissError();
    try {
      const res = await fetch("/api/packs/open", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ packId }),
      });
      if (!res.ok) {
        let message = "Could not open pack right now.";
        try {
          const err = await res.json() as { error?: string };
          if (typeof err.error === "string" && err.error.trim()) {
            message = err.error;
          }
        } catch {
          // Use fallback message
        }
        showError(message);
        return;
      }
      const data = (await res.json()) as PackResult;
      setPackCards(data.cards);
      setLastOpenedPack(data.pack);
      setUser((prev) =>
        prev ? { ...prev, coins: data.coinsRemaining } : prev
      );
      setView("opening");
    } catch (error) {
      console.error(error);
      showError("Could not open pack right now.");
    } finally {
      setLoading(false);
    }
  }, [dismissError, showError]);

  const coins = user?.coins ?? 0;
  const defaultPack = packs.find((pack) => pack.id === DEFAULT_PACK_ID) ?? packs[0] ?? PACK_DEFINITIONS[0];
  const openingPack = lastOpenedPack ?? (
    defaultPack
      ? {
          id: defaultPack.id,
          name: defaultPack.name,
          cost: defaultPack.cost,
          cardsPerPack: defaultPack.cardsPerPack,
        }
      : null
  );

  const handleCoinsUpdate = useCallback((newCoins: number) => {
    setUser((prev) => (prev ? { ...prev, coins: newCoins } : prev));
  }, []);

  return (
    <div className="min-h-screen w-full flex flex-col">
      <NavBar
        currentView={view}
        onNavigate={handleNavigate}
        coins={coins}
        canOpenPacks={Boolean(packCards)}
      />

      {appError && (
        <div className="border-b border-destructive/40 bg-destructive/10">
          <div className="max-w-5xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
            <p className="text-sm text-destructive">{appError}</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-destructive hover:text-destructive"
              onClick={dismissError}
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      <main className="flex-1">
        {view === "shop" && (
          <PackShop
            packs={packs}
            coins={coins}
            onBuyPack={buyPack}
            loading={loading}
          />
        )}

        {view === "opening" && packCards && (
          <PackOpening
            cards={packCards}
            pack={openingPack}
            onDone={() => setView("collection")}
            onOpenAnother={() => {
              if (!openingPack) return;
              void buyPack(openingPack.id);
            }}
            canAfford={openingPack ? coins >= openingPack.cost : false}
            loading={loading}
            openAnotherLabel={
              openingPack
                ? `Open Another ${openingPack.name} (${openingPack.cost.toLocaleString()} coins)`
                : "Open Another"
            }
          />
        )}

        {view === "opening" && !packCards && (
          <div className="flex flex-col items-center justify-center py-20 gap-4 px-4 text-center">
            <h3 className="text-xl font-semibold">No pack ready</h3>
            <p className="text-muted-foreground">
              Open a pack in the shop first, then come back here to reveal it.
            </p>
            <Button
              type="button"
              onClick={() => setView("shop")}
            >
              Go to Shop
            </Button>
          </div>
        )}

        {view === "collection" && (
          <Collection
            data={collection}
            loading={collectionLoading}
            onGoToShop={() => setView("shop")}
          />
        )}

        {view === "hustle" && (
          <HustleView
            data={hustleData}
            coins={coins}
            loading={hustleLoading}
            onRefresh={fetchHustle}
            onCoinsUpdate={handleCoinsUpdate}
            onError={showError}
          />
        )}

        {view === "battle" && (
          <BattleView
            data={hustleData}
            loading={hustleLoading}
            onRefresh={fetchHustle}
            onGoToHustle={() => setView("hustle")}
          />
        )}
      </main>
    </div>
  );
}
